import { Button, Dialog, DialogContent, DialogTitle, FormHelperText, MenuItem, Select, Stack } from '@mui/material';
import { graphql } from '../../../gql';
import { Widget, WidgetType } from '../../../gql/graphql';
import { useApiMutation } from '../../../query/hooks';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { widgetDefinitions } from './widgets/widgetDefinitions';
import { mutationResponseHandler } from '../../../utils/mutationResponseHandler';
import { useSnackbar } from '../../../store/snackbar';

type Props = Readonly<{
	open: boolean;
	onClose: () => void;
	id: string;
	widgets: readonly Widget[];
}>;

const journeyDetail_createWidget = graphql(`
	mutation journeyDetail_createWidget($type: WidgetType!, $journeyId: UUID!, $order: Int!) {
		createWidget(data: { order: $order, type: $type, journey: { connect: { id: $journeyId } } }) {
			ok
			errorMessage
		}
	}
`);

export const AddWidget: FC<Props> = ({ open, onClose, id, widgets }) => {
	const { mutate, isPending } = useApiMutation(journeyDetail_createWidget);
	const [selectedField, setSelectedField] = useState<string>('');
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const handleSubmit = () => {
		setSelectedField('');
		mutate(
			{ type: selectedField as WidgetType, journeyId: id, order: widgets.length },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.createWidget.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'journey.detail.widget.add.success' }),
							error: intl.formatMessage({ id: 'journey.detail.widget.add.error' }),
						},
						errorMessage: result.createWidget.errorMessage,
						callback: onClose,
						openSnackbar,
					});
				},
			},
		);
	};

	const availableWidgetTypes = Object.values(widgetDefinitions).filter(
		({ type }) => !widgets.find((widget) => widget.type === type) && type !== 'empty',
	);

	return (
		<Dialog open={open} maxWidth={'sm'} onClose={onClose}>
			<DialogContent sx={{ mt: 2, my: 1 }}>
				<Stack direction={'column'} spacing={2}>
					<DialogTitle>
						<FormattedMessage id={'journey.detail.add-widget-modal.title'} />
					</DialogTitle>
					<Select
						value={selectedField}
						disabled={availableWidgetTypes.length === 0}
						variant={'outlined'}
						onChange={({ target: { value } }) => setSelectedField(value)}
					>
						{availableWidgetTypes.map(({ type, name }) => (
							<MenuItem key={type} value={type}>
								{name}
							</MenuItem>
						))}
					</Select>
					{availableWidgetTypes.length === 0 && (
						<FormHelperText>
							<FormattedMessage id={'journey.detail.add-widget-modal.no-widgets'} />
						</FormHelperText>
					)}
					<Button variant={'contained'} onClick={handleSubmit} disabled={selectedField === '' || isPending}>
						<FormattedMessage id={'common.add'} />
					</Button>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
